(function () {
  'use strict';


  const CONFIG = Object.freeze({
    makeParam:
      'filter.p.m.custom.car_make',

    modelParam:
      'filter.p.m.custom.car_model',

    universalValue:
      'Universal'
  });


  /*
   * =========================================================
   * STATE
   * =========================================================
   */

  let scheduledFrame =
    null;

  let emptyStateRunId =
    0;

  let activeEmptyHost =
    null;

  const unfilteredCollectionCache =
    new Map();


  /*
   * =========================================================
   * HELPERS
   * =========================================================
   */

  const getActiveVehicle = () => {

    if (
      !window.LCMVehicle ||
      typeof window.LCMVehicle.getActive !==
        'function'
    ) {
      return null;
    }


    return window.LCMVehicle.getActive();
  };


  const getCurrentUrl = () => {

    return new URL(
      window.location.href
    );
  };


  const formatInternalUrl = (
    url
  ) => {

    return (
      url.pathname +
      url.search +
      url.hash
    );
  };


  const normalizeValues = (
    values
  ) => {

    return [...values]
      .map(
        (value) =>
          String(value)
      )
      .sort();
  };


  const sameValues = (
    a,
    b
  ) => {

    const valuesA =
      normalizeValues(a);

    const valuesB =
      normalizeValues(b);


    if (
      valuesA.length !==
      valuesB.length
    ) {
      return false;
    }


    return valuesA.every(
      (
        value,
        index
      ) =>
        value ===
        valuesB[index]
    );
  };


  const getVehicleName = (
    vehicle
  ) => {

    if (!vehicle) {
      return '';
    }


    return [
      vehicle.make,
      vehicle.model
    ]
      .filter(Boolean)
      .join(' ')
      .trim();
  };


  /*
   * =========================================================
   * LANGUAGE
   * =========================================================
   */

  const getLanguage = () => {

    const language =
      String(
        document.documentElement.lang ||
        'nl'
      )
        .toLowerCase();


    if (
      language.startsWith('en')
    ) {
      return 'en';
    }


    if (
      language.startsWith('de')
    ) {
      return 'de';
    }


    return 'nl';
  };


  const getTranslations = (
    vehicle
  ) => {

    const vehicleName =
      getVehicleName(
        vehicle
      );


    const translations = {

      nl: {
        vehicleTitle:
          `Geen producten voor jouw ${vehicleName}`,

        vehicleText:
          'In deze categorie hebben we momenteel geen passende of universele producten voor jouw auto.',

        filteredTitle:
          'Geen producten met deze filters',

        filteredVehicleText:
          `De combinatie van jouw ${vehicleName} en de gekozen filters levert geen resultaten op.`,

        filteredText:
          'De gekozen filters leveren geen resultaten op. Wis de filters om het volledige aanbod in deze categorie te bekijken.',

        collectionTitle:
          'Nog geen producten in deze categorie',

        collectionText:
          'Ons assortiment groeit voortdurend. Neem gerust contact op als je iets specifieks zoekt.',

        changeVehicle:
          'Wijzig auto',

        showAll:
          'Toon volledig assortiment',

        clearFilters:
          'Wis overige filters',

        clearAllFilters:
          'Wis filters',

        viewAll:
          'Bekijk alle onderdelen'
      },


      en: {
        vehicleTitle:
          `No products for your ${vehicleName}`,

        vehicleText:
          'We currently have no matching or universal products for your vehicle in this category.',

        filteredTitle:
          'No products with these filters',

        filteredVehicleText:
          `The combination of your ${vehicleName} and the selected filters returned no results.`,

        filteredText:
          'The selected filters returned no results. Clear the filters to view the full range in this category.',

        collectionTitle:
          'No products in this category yet',

        collectionText:
          'Our range is constantly growing. Feel free to contact us if you are looking for something specific.',

        changeVehicle:
          'Change vehicle',

        showAll:
          'Show full range',

        clearFilters:
          'Clear other filters',

        clearAllFilters:
          'Clear filters',

        viewAll:
          'View all parts'
      },


      de: {
        vehicleTitle:
          `Keine Produkte für deinen ${vehicleName}`,

        vehicleText:
          'In dieser Kategorie haben wir derzeit keine passenden oder universellen Produkte für dein Fahrzeug.',

        filteredTitle:
          'Keine Produkte mit diesen Filtern',

        filteredVehicleText:
          `Die Kombination aus deinem ${vehicleName} und den ausgewählten Filtern liefert keine Ergebnisse.`,

        filteredText:
          'Die ausgewählten Filter liefern keine Ergebnisse. Entferne die Filter, um das gesamte Sortiment dieser Kategorie zu sehen.',

        collectionTitle:
          'Noch keine Produkte in dieser Kategorie',

        collectionText:
          'Unser Sortiment wächst ständig. Kontaktiere uns gerne, wenn du etwas Bestimmtes suchst.',

        changeVehicle:
          'Fahrzeug ändern',

        showAll:
          'Gesamtes Sortiment anzeigen',

        clearFilters:
          'Weitere Filter löschen',

        clearAllFilters:
          'Filter löschen',

        viewAll:
          'Alle Teile ansehen'
      }
    };


    return translations[
      getLanguage()
    ];
  };


  /*
   * =========================================================
   * IS THIS URL MANAGED BY LCM VEHICLE STATE?
   * =========================================================
   */

  const isManagedVehicleUrl = (
    vehicle,
    url
  ) => {

    if (
      !vehicle ||
      !vehicle.model
    ) {
      return false;
    }


    const models =
      url.searchParams.getAll(
        CONFIG.modelParam
      );


    return (
      models.includes(
        vehicle.model
      ) &&
      models.includes(
        CONFIG.universalValue
      )
    );
  };


  /*
   * =========================================================
   * DETECT A CAR_MODEL FACET REMOVE LINK
   * =========================================================
   */

  const removesModelFilter = (
    link,
    currentUrl
  ) => {

    if (
      !link ||
      !link.href
    ) {
      return false;
    }


    let destination;


    try {

      destination =
        new URL(
          link.href,
          window.location.origin
        );

    } catch (error) {

      return false;
    }


    const currentModels =
      currentUrl.searchParams.getAll(
        CONFIG.modelParam
      );


    const destinationModels =
      destination.searchParams.getAll(
        CONFIG.modelParam
      );


    return !sameValues(
      currentModels,
      destinationModels
    );
  };


  /*
   * =========================================================
   * FILTER DETECTION
   * =========================================================
   */

  const hasNonVehicleFilters = (
    url
  ) => {

    for (
      const [key] of
      url.searchParams.entries()
    ) {

      if (
        !key.startsWith(
          'filter.'
        )
      ) {
        continue;
      }


      if (
        key ===
          CONFIG.modelParam ||
        key ===
          CONFIG.makeParam
      ) {
        continue;
      }


      return true;
    }


    return false;
  };


  const hasAnyCollectionFilters = (
    url
  ) => {

    for (
      const [key] of
      url.searchParams.entries()
    ) {

      if (
        key.startsWith(
          'filter.'
        )
      ) {
        return true;
      }
    }


    return false;
  };


  /*
   * =========================================================
   * CLEAR NON-VEHICLE FILTERS
   * =========================================================
   */

  const buildClearNonVehicleFiltersUrl =
    (
      vehicle
    ) => {

      const url =
        getCurrentUrl();


      const keys =
        Array.from(
          new Set(
            url.searchParams.keys()
          )
        );


      keys.forEach(
        (
          key
        ) => {

          if (
            key.startsWith(
              'filter.'
            )
          ) {

            url.searchParams.delete(
              key
            );
          }
        }
      );


      url.searchParams.append(
        CONFIG.modelParam,
        vehicle.model
      );


      url.searchParams.append(
        CONFIG.modelParam,
        CONFIG.universalValue
      );


      url.searchParams.delete(
        'page'
      );


      return formatInternalUrl(
        url
      );
    };


  /*
   * =========================================================
   * CLEAR ALL FILTERS
   * =========================================================
   */

  const buildClearAllFiltersUrl =
    () => {

      const url =
        getCurrentUrl();


      const keys =
        Array.from(
          new Set(
            url.searchParams.keys()
          )
        );


      keys.forEach(
        (
          key
        ) => {

          if (
            key.startsWith(
              'filter.'
            )
          ) {

            url.searchParams.delete(
              key
            );
          }
        }
      );


      url.searchParams.delete(
        'page'
      );


      return formatInternalUrl(
        url
      );
    };


  /*
   * =========================================================
   * ALL PRODUCTS URL
   * =========================================================
   */

  const buildAllProductsUrl = () => {

    const parts =
      window.location.pathname
        .split('/')
        .filter(Boolean);


    let localePrefix =
      '';


    if (
      parts.length &&
      /^[a-z]{2}(?:-[a-z]{2})?$/i
        .test(
          parts[0]
        )
    ) {

      localePrefix =
        '/' + parts[0];
    }


    return (
      localePrefix +
      '/collections/all'
    );
  };


  /*
   * =========================================================
   * STORE / RESTORE LINK
   * =========================================================
   */

  const storeOriginalHref = (
    link
  ) => {

    if (
      link.dataset
        .lcmOriginalHref !==
      undefined
    ) {
      return;
    }


    link.dataset.lcmOriginalHref =
      link.getAttribute(
        'href'
      ) || '';
  };


  const restoreOriginalHref = (
    link
  ) => {

    if (
      link.dataset
        .lcmOriginalHref ===
      undefined
    ) {
      return;
    }


    link.setAttribute(
      'href',
      link.dataset.lcmOriginalHref
    );


    delete link.dataset
      .lcmOriginalHref;
  };


  /*
   * =========================================================
   * ACTIVE FACETS
   * =========================================================
   */

  const cleanActiveFacets = (
    vehicle,
    currentUrl,
    managed
  ) => {

    document
      .querySelectorAll(
        '#MainContent .active-facets'
      )
      .forEach(
        (
          container
        ) => {

          let visibleFacetCount =
            0;


          container
            .querySelectorAll(
              'facet-remove'
            )
            .forEach(
              (
                facetRemove
              ) => {

                const link =
                  facetRemove.querySelector(
                    'a.active-facets__button'
                  );


                if (!link) {
                  return;
                }


                const isVehicleFacet =
                  managed &&
                  removesModelFilter(
                    link,
                    currentUrl
                  );


                facetRemove
                  .classList
                  .toggle(
                    'lcm-vehicle-facet-hidden',
                    isVehicleFacet
                  );


                if (
                  !isVehicleFacet
                ) {
                  visibleFacetCount +=
                    1;
                }
              }
            );


          /*
           * ALLES VERWIJDEREN
           */

          container
            .querySelectorAll(
              'facet-remove.active-facets__button-wrapper'
            )
            .forEach(
              (
                wrapper
              ) => {

                const link =
                  wrapper.querySelector(
                    'a.active-facets__button-remove'
                  );


                if (!link) {
                  return;
                }


                if (!managed) {

                  wrapper.classList.remove(
                    'lcm-vehicle-clear-hidden'
                  );


                  restoreOriginalHref(
                    link
                  );


                  return;
                }


                if (
                  visibleFacetCount ===
                  0
                ) {

                  wrapper.classList.add(
                    'lcm-vehicle-clear-hidden'
                  );


                  return;
                }


                wrapper.classList.remove(
                  'lcm-vehicle-clear-hidden'
                );


                storeOriginalHref(
                  link
                );


                link.setAttribute(
                  'href',
                  buildClearNonVehicleFiltersUrl(
                    vehicle
                  )
                );
              }
            );


          container.classList.toggle(
            'lcm-vehicle-active-facets-empty',
            managed &&
              visibleFacetCount ===
                0
          );
        }
      );
  };


  /*
   * =========================================================
   * MOBILE "ALLES VERWIJDEREN"
   * =========================================================
   */

  const cleanMobileClearButtons = (
    vehicle,
    currentUrl,
    managed
  ) => {

    const hasOtherFilters =
      hasNonVehicleFilters(
        currentUrl
      );


    document
      .querySelectorAll(
        '#MainContent .mobile-facets__clear-wrapper'
      )
      .forEach(
        (
          wrapper
        ) => {

          const link =
            wrapper.querySelector(
              'a.mobile-facets__clear'
            );


          if (!link) {
            return;
          }


          if (!managed) {

            wrapper.classList.remove(
              'lcm-vehicle-clear-hidden'
            );


            restoreOriginalHref(
              link
            );


            return;
          }


          if (
            !hasOtherFilters
          ) {

            wrapper.classList.add(
              'lcm-vehicle-clear-hidden'
            );


            return;
          }


          wrapper.classList.remove(
            'lcm-vehicle-clear-hidden'
          );


          storeOriginalHref(
            link
          );


          link.setAttribute(
            'href',
            buildClearNonVehicleFiltersUrl(
              vehicle
            )
          );
        }
      );
  };


  /*
   * =========================================================
   * PRODUCT GRID
   * =========================================================
   */

  const getProductGridContainer =
    () => {

      return document.querySelector(
        '#MainContent #ProductGridContainer'
      );
    };


  const containerHasProducts = (
    container
  ) => {

    if (!container) {
      return false;
    }


    /*
     * Productkaarten linken altijd naar /products/.
     *
     * Hiermee hoeven we niet afhankelijk te zijn van
     * Ignite's vertaalde "Geen producten" tekst.
     */

    return !!container.querySelector(
      'a[href*="/products/"]'
    );
  };


  /*
   * =========================================================
   * UNFILTERED COLLECTION CHECK
   * =========================================================
   *
   * Als een actief voertuig 0 resultaten geeft willen we
   * weten:
   *
   * - bestaat er in deze collectie überhaupt assortiment?
   * - of bestaan er alleen geen producten voor deze auto?
   *
   * Alleen bij een lege vehicle-resultaatpagina doen we
   * daarvoor één achtergrondrequest naar de ongefilterde
   * collection.
   */

  const unfilteredCollectionHasProducts =
    (
      pathname
    ) => {

      if (
        unfilteredCollectionCache.has(
          pathname
        )
      ) {

        return unfilteredCollectionCache.get(
          pathname
        );
      }


      const promise =
        fetch(
          pathname,
          {
            credentials:
              'same-origin'
          }
        )
          .then(
            (
              response
            ) => {

              if (
                !response.ok
              ) {

                throw new Error(
                  String(
                    response.status
                  )
                );
              }


              return response.text();
            }
          )
          .then(
            (
              responseText
            ) => {

              const html =
                new DOMParser()
                  .parseFromString(
                    responseText,
                    'text/html'
                  );


              const container =
                html.querySelector(
                  '#ProductGridContainer'
                );


              return containerHasProducts(
                container
              );
            }
          )
          .catch(
            () => {

              /*
               * Bij een netwerkfout nemen we aan dat de
               * collection wel producten bevat.
               *
               * Zo tonen we liever de voertuigmelding
               * dan ten onrechte "lege categorie".
               */

              return true;
            }
          );


      unfilteredCollectionCache.set(
        pathname,
        promise
      );


      return promise;
    };


  /*
   * =========================================================
   * EMPTY STATE HOST
   * =========================================================
   */

  const getEmptyStateHost = (
    productGridContainer
  ) => {

    if (
      !productGridContainer
    ) {
      return null;
    }


    /*
     * Bij verticale Ignite-filters bevat .facets-vertical
     * zowel de filterkolom als de productkolom.
     *
     * Door ons blok daar te plaatsen kunnen we bij 0
     * resultaten de volledige breedte gebruiken.
     */

    return (
      productGridContainer.closest(
        '.facets-vertical'
      ) ||
      productGridContainer.parentElement
    );
  };


  const clearEmptyState =
    () => {

      emptyStateRunId +=
        1;


      if (
        activeEmptyHost
      ) {

        activeEmptyHost.classList.remove(
          'lcm-collection-empty-host'
        );


        const state =
          activeEmptyHost.querySelector(
            ':scope > .lcm-collection-empty-state'
          );


        if (state) {
          state.remove();
        }


        activeEmptyHost =
          null;
      }
    };


  /*
   * =========================================================
   * EMPTY STATE ICON
   * =========================================================
   */

  const createEmptyStateIcon =
    (
      type
    ) => {

      const icon =
        document.createElement(
          'div'
        );


      icon.className =
        'lcm-collection-empty-state__icon';


      if (
        type ===
        'vehicle'
      ) {

        icon.innerHTML = `
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M5.5 16.5h13M7 16.5v1.75M17 16.5v1.75M4.75 13.25l1.35-4.1A2 2 0 0 1 8 7.75h8a2 2 0 0 1 1.9 1.4l1.35 4.1M5.25 13.25h13.5a1.5 1.5 0 0 1 1.5 1.5v1.75H3.75v-1.75a1.5 1.5 0 0 1 1.5-1.5ZM7 14.75h.01M17 14.75h.01"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        `;

      } else if (
        type ===
        'filtered'
      ) {

        icon.innerHTML = `
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M4 6h16M7 12h10M10 18h4"
              fill="none"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />
          </svg>
        `;

      } else {

        icon.innerHTML = `
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M4.5 8.5 12 4l7.5 4.5v7L12 20l-7.5-4.5v-7ZM4.8 8.6 12 13l7.2-4.4M12 13v7"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        `;
      }


      return icon;
    };


  /*
   * =========================================================
   * EMPTY STATE CONTENT
   * =========================================================
   */

  const getEmptyStateContent =
    (
      type,
      vehicle
    ) => {

      const text =
        getTranslations(
          vehicle
        );


      if (
        type ===
        'vehicle'
      ) {

        return {
          icon:
            'vehicle',

          title:
            text.vehicleTitle,

          description:
            text.vehicleText,

          actions: [
            {
              label:
                text.changeVehicle,

              action:
                'change-vehicle',

              primary:
                true
            },
            {
              label:
                text.showAll,

              action:
                'clear-vehicle',

              primary:
                false
            }
          ]
        };
      }


      if (
        type ===
        'vehicle-filters'
      ) {

        return {
          icon:
            'filtered',

          title:
            text.filteredTitle,

          description:
            text.filteredVehicleText,

          actions: [
            {
              label:
                text.clearFilters,

              action:
                'clear-nonvehicle',

              primary:
                true
            },
            {
              label:
                text.changeVehicle,

              action:
                'change-vehicle',

              primary:
                false
            }
          ]
        };
      }


      if (
        type ===
        'filters'
      ) {

        return {
          icon:
            'filtered',

          title:
            text.filteredTitle,

          description:
            text.filteredText,

          actions: [
            {
              label:
                text.clearAllFilters,

              action:
                'clear-all',

              primary:
                true
            }
          ]
        };
      }


      return {
        icon:
          'collection',

        title:
          text.collectionTitle,

        description:
          text.collectionText,

        actions: [
          {
            label:
              text.viewAll,

            action:
              'view-all',

            primary:
              true
          }
        ]
      };
    };


  /*
   * =========================================================
   * RENDER EMPTY STATE
   * =========================================================
   */

  const renderEmptyState =
    (
      host,
      type,
      vehicle
    ) => {

      if (!host) {
        return;
      }


      if (
        activeEmptyHost &&
        activeEmptyHost !== host
      ) {

        clearEmptyState();
      }


      activeEmptyHost =
        host;


      host.classList.add(
        'lcm-collection-empty-host'
      );


      let state =
        host.querySelector(
          ':scope > .lcm-collection-empty-state'
        );


      if (!state) {

        state =
          document.createElement(
            'div'
          );


        state.className =
          'lcm-collection-empty-state';


        host.appendChild(
          state
        );
      }


      const content =
        getEmptyStateContent(
          type,
          vehicle
        );


      const signature =
        [
          type,
          content.title,
          content.description
        ].join('|');


      if (
        state.dataset
          .lcmSignature ===
        signature
      ) {
        return;
      }


      state.dataset
        .lcmSignature =
        signature;


      state.replaceChildren();


      const inner =
        document.createElement(
          'div'
        );


      inner.className =
        'lcm-collection-empty-state__inner';


      inner.appendChild(
        createEmptyStateIcon(
          content.icon
        )
      );


      const title =
        document.createElement(
          'h2'
        );


      title.className =
        'lcm-collection-empty-state__title';


      title.textContent =
        content.title;


      inner.appendChild(
        title
      );


      const description =
        document.createElement(
          'p'
        );


      description.className =
        'lcm-collection-empty-state__text';


      description.textContent =
        content.description;


      inner.appendChild(
        description
      );


      if (
        content.actions.length
      ) {

        const actions =
          document.createElement(
            'div'
          );


        actions.className =
          'lcm-collection-empty-state__actions';


        content.actions.forEach(
          (
            item
          ) => {

            const button =
              document.createElement(
                'button'
              );


            button.type =
              'button';


            button.className =
              item.primary
                ? 'button button--primary lcm-collection-empty-state__button'
                : 'button button--secondary lcm-collection-empty-state__button';


            button.dataset
              .lcmEmptyAction =
              item.action;


            button.textContent =
              item.label;


            actions.appendChild(
              button
            );
          }
        );


        inner.appendChild(
          actions
        );
      }


      state.appendChild(
        inner
      );
    };


  /*
   * =========================================================
   * EMPTY STATE LOGIC
   * =========================================================
   */

  const updateEmptyState =
    async (
      vehicle,
      currentUrl,
      managed
    ) => {

      const runId =
        ++emptyStateRunId;


      const productGridContainer =
        getProductGridContainer();


      /*
       * Geen collection product grid.
       */
      if (
        !productGridContainer
      ) {

        clearEmptyState();

        return;
      }


      /*
       * Er zijn producten:
       * normale Ignite-layout tonen.
       */
      if (
        containerHasProducts(
          productGridContainer
        )
      ) {

        clearEmptyState();

        return;
      }


      const host =
        getEmptyStateHost(
          productGridContainer
        );


      if (!host) {
        return;
      }


      const hasOtherFilters =
        hasNonVehicleFilters(
          currentUrl
        );


      /*
       * Actief voertuig + extra filters.
       */
      if (
        managed &&
        hasOtherFilters
      ) {

        renderEmptyState(
          host,
          'vehicle-filters',
          vehicle
        );

        return;
      }


      /*
       * Geen LCM vehicle-context maar wel gewone filters.
       */
      if (
        !managed &&
        hasAnyCollectionFilters(
          currentUrl
        )
      ) {

        renderEmptyState(
          host,
          'filters',
          vehicle
        );

        return;
      }


      /*
       * Actief voertuig zonder overige filters.
       *
       * Nu controleren we of de collectie zelf wel
       * producten bevat.
       */
      if (managed) {

        const collectionHasProducts =
          await unfilteredCollectionHasProducts(
            currentUrl.pathname
          );


        /*
         * Intussen kan via AJAX alweer een andere
         * filterstatus actief zijn.
         */
        if (
          runId !==
          emptyStateRunId
        ) {
          return;
        }


        renderEmptyState(
          host,
          collectionHasProducts
            ? 'vehicle'
            : 'collection',
          vehicle
        );


        return;
      }


      /*
       * Geen filters + geen producten:
       * collectie zelf is leeg.
       */
      renderEmptyState(
        host,
        'collection',
        vehicle
      );
    };


  /*
   * =========================================================
   * EMPTY STATE BUTTON ACTIONS
   * =========================================================
   */

  const handleEmptyStateAction =
    (
      event
    ) => {

      const button =
        event.target.closest(
          '[data-lcm-empty-action]'
        );


      if (!button) {
        return;
      }


      const action =
        button.dataset
          .lcmEmptyAction;


      /*
       * WIJZIG AUTO
       */

      if (
        action ===
        'change-vehicle'
      ) {

        const changeButton =
          document.querySelector(
            '.lcm-vehicle-status__change'
          );


        if (changeButton) {

          changeButton.click();


          const status =
            document.querySelector(
              '.lcm-vehicle-status'
            );


          if (status) {

            status.scrollIntoView({
              behavior:
                'smooth',

              block:
                'center'
            });
          }
        }


        return;
      }


      /*
       * WIS AUTO + VOLLEDIG ASSORTIMENT
       *
       * Gebruik bewust de bestaande LCM-knop.
       * Die wist zowel localStorage als de URL correct.
       */

      if (
        action ===
        'clear-vehicle'
      ) {

        const clearVehicleButton =
          document.querySelector(
            '.lcm-vehicle-status__clear'
          );


        if (clearVehicleButton) {

          clearVehicleButton.click();
        }


        return;
      }


      /*
       * WIS ALLEEN OVERIGE FILTERS
       */

      if (
        action ===
        'clear-nonvehicle'
      ) {

        const vehicle =
          getActiveVehicle();


        if (!vehicle) {
          return;
        }


        window.location.assign(
          buildClearNonVehicleFiltersUrl(
            vehicle
          )
        );


        return;
      }


      /*
       * WIS ALLE FILTERS
       */

      if (
        action ===
        'clear-all'
      ) {

        window.location.assign(
          buildClearAllFiltersUrl()
        );


        return;
      }


      /*
       * ALLE ONDERDELEN
       */

      if (
        action ===
        'view-all'
      ) {

        window.location.assign(
          buildAllProductsUrl()
        );
      }
    };


  /*
   * =========================================================
   * MAIN CLEANUP
   * =========================================================
   */

  const cleanupCollectionVehicleUi =
    () => {

      const vehicle =
        getActiveVehicle();


      const currentUrl =
        getCurrentUrl();


      const managed =
        isManagedVehicleUrl(
          vehicle,
          currentUrl
        );


      cleanActiveFacets(
        vehicle,
        currentUrl,
        managed
      );


      cleanMobileClearButtons(
        vehicle,
        currentUrl,
        managed
      );


      updateEmptyState(
        vehicle,
        currentUrl,
        managed
      );
    };


  /*
   * =========================================================
   * DEBOUNCE / AJAX FACET UPDATES
   * =========================================================
   */

  const scheduleCleanup =
    () => {

      if (
        scheduledFrame !==
        null
      ) {
        return;
      }


      scheduledFrame =
        window.requestAnimationFrame(
          () => {

            scheduledFrame =
              null;


            cleanupCollectionVehicleUi();
          }
        );
    };


  /*
   * =========================================================
   * INITIALIZE
   * =========================================================
   */

  const initialize =
    () => {

      document.addEventListener(
        'click',
        handleEmptyStateAction
      );


      scheduleCleanup();


      const main =
        document.querySelector(
          '#MainContent'
        );


      if (!main) {
        return;
      }


      const observer =
        new MutationObserver(
          () => {

            scheduleCleanup();
          }
        );


      observer.observe(
        main,
        {
          childList:
            true,

          subtree:
            true
        }
      );
    };


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );

  } else {

    initialize();
  }


  /*
   * Vehicle state verandert.
   */

  window.addEventListener(
    'lcm:vehiclechange',
    scheduleCleanup
  );


  /*
   * Browser back/forward.
   */

  window.addEventListener(
    'popstate',
    scheduleCleanup
  );


  /*
   * Shopify Theme Editor.
   */

  document.addEventListener(
    'shopify:section:load',
    scheduleCleanup
  );

})();